import { escapeHtml } from '../inventoree/format'
import { dash, durationString, fromNowOrDash, guard, shortDuration, temperature, textOrDash } from './format'

/**
 * Ultima conectare, asa cum o trimite `/api/v2/opteemum/*` - forma o face
 * `Application\Services\Device\LastLogin::shape`, in PHP, pentru amandoua
 * portalurile.
 */
export interface LastLogin {
  user: string
  type: string
  detail: string
  at: string
  age_seconds: number | null
  open: boolean
  online: boolean
  open_count: number
}

/**
 * "Cum" s-a conectat: tipul, fara paranteza de la coada, si ora.
 *
 * Tipul vine uneori ca "SSH password (10.0.0.7)" - amprenta e deja in
 * `detail`, si scrisa de doua ori pe acelasi rand n-ar incapea.
 */
export function loginHow(login: LastLogin): string {
  const t = String(login.type || '').replace(/\s*\([^)]*\)\s*$/, '')

  if (t && login.at) {
    return t + ' \u00b7 ' + login.at
  }

  return t || login.at || ''
}

/**
 * Coloana "Last login": cine, cu ce semn, si dedesubt cum si cand.
 *
 * Semnul verde inseamna „chiar acum" - randul e deschis SI masina a vorbit in
 * ultimul sfert de ora. Cel gri inseamna „era conectat, dar masina tace de
 * atunci": un rand ramas deschis pe o statie oprita de la priza nu e o
 * sesiune vie. Hotararea se ia pe server (`LastLogin::shape`), aici doar se
 * deseneaza.
 *
 * Fara niciun rand de jurnal se cade pe `UserLogat` din inventar, scris gri si
 * spus ca atare: e un nume, nu o conectare.
 */
export function lastLoginCell(r: DeviceRow): string {
  const login = r.last_login

  if (!login || !login.user) {
    if (r.UserLogat) {
      return (
        '<span class="color-light-grey" data-toggle="tooltip" data-placement="top"' +
        ' title="From the inventory - no login time recorded yet">' +
        escapeHtml(r.UserLogat) +
        '</span>'
      )
    }

    return dash()
  }

  let mark = ''

  if (login.online) {
    const open = login.open_count > 1 ? login.open_count + ' sessions open' : 'Still logged in'

    mark =
      ' <i class="fas fa-circle color-green2 font-10" data-toggle="tooltip" data-placement="top"' +
      ' title="' + escapeHtml(open) + '"></i>'
  } else if (login.open) {
    mark =
      ' <i class="fas fa-circle color-light-grey font-10" data-toggle="tooltip" data-placement="top"' +
      ' title="Was logged in - the machine has not reported since"></i>'
  }

  const how = loginHow(login)
  const tip = [login.type, login.detail].filter(Boolean).join(' \u00b7 ')

  return (
    '<p style="margin:0">' + escapeHtml(login.user) + mark + '</p>' +
    '<span class="color-light-grey font-11"' +
    (tip ? ' data-toggle="tooltip" data-placement="top" title="' + escapeHtml(tip) + '"' : '') +
    '>' + escapeHtml(how) + '</span>'
  )
}

export interface DeviceRow {
  deviceId: number
  DT_RowId: string
  Tip: number
  device_type: string
  device_icon: string
  actions: string
  Denumire: string
  ownerId: number
  Prenume: string | null
  Nume: string | null
  UserLogat: string | null
  office: string | null
  DeviceGroup: string | null
  operating_system: string | null
  os_available_updates: number | string | null
  installed_applications: number | string | null
  wei: number | string | null
  last_bootup_time: number | string | null
  last_shutdown_time: number | string | null
  cpu_temp: number | string | null
  hdd_temp: number | string | null
  antivirus_status: number | string | null
  firewall_status: number | string | null
  startup_applications: number | string | null
  last_critical_logs: number | string | null
  changes_24h: number | string | null
  DataAccesareDM: string | null
  last_login?: LastLogin | null
  external_ip: string | null
  internal_ip: string | null
  uptime_seconds: number | string | null
  server_service?: {
    role?: { label?: string; matched?: string }
    status?: string
    health?: { summary?: string }
    reasons?: { text: string }[]
    next_action?: { text?: string; severity?: string }
    optimizations?: { title?: string }[]
    upgrade?: { importance?: string; current?: string; available?: string; label?: string }
  }
  server_role?: { label?: string; matched?: string }
  storage?: { smart?: string | null; partition?: { percent?: number; name?: string; free?: number; size?: number } }
  __checked?: boolean
  [key: string]: unknown
}

export interface Column {
  index: number
  title: string
  titleHtml?: string
  name?: string
  orderable: boolean
  visible: boolean
  width?: number
  html(row: DeviceRow): string
}

function ownerName(row: DeviceRow): string {
  if (row.ownerId > 0) {
    return `<p>${escapeHtml(row.Denumire)}</p><span style="cursor:pointer" class="color-light-grey quick-sidebar-owner" data-id="${row.ownerId}"><i class="fa fa-user"></i> ${escapeHtml(row.Prenume)} ${escapeHtml(row.Nume)}</span>`
  }
  return `<p>${escapeHtml(row.Denumire)}</p><span class="color-red"><i class="fa fa-user"></i> Unassigned</span>`
}

function countLabel(value: unknown, cls: string, prefix = ''): string {
  if (!value) return dash()
  return `<span class="label ${cls}">${prefix}${escapeHtml(value)}</span>`
}

function bootMs(value: unknown): string {
  const n = Number(value)
  if (!n || n <= 0) return dash()
  return durationString(Math.floor(n / 1000))
}

export function deviceColumns(now: () => Date = () => new Date()): Column[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${r.deviceId}" data-type="${r.Tip}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Type',
      name: 'Tip',
      orderable: true,
      visible: true,
      width: 80,
      html: (r) =>
        `<center data-toggle="tooltip" data-id="${r.deviceId}" class="quick-sidebar-device" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon} <i class="fas font-16 fa-bolt color-danger" id="sconnection_${r.deviceId}"></i></center>`,
    },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: ownerName },
    { index: 3, title: 'Last User Logged On', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash(r.UserLogat) },
    { index: 4, title: 'Office', name: 'office', orderable: true, visible: true, html: (r) => textOrDash(r.office) },
    { index: 5, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 6, title: 'Operating system', name: 'operating_system', orderable: true, visible: true, html: (r) => textOrDash(r.operating_system) },
    { index: 7, title: 'OS Available Updates', name: 'os_available_updates', orderable: true, visible: true, html: (r) => countLabel(r.os_available_updates, 'label-warning') },
    { index: 8, title: 'Installed Applications', name: 'installed_applications', orderable: true, visible: true, html: (r) => countLabel(r.installed_applications, 'label-success') },
    { index: 9, title: 'WEI', name: 'wei', orderable: true, visible: true, html: (r) => textOrDash(r.wei) },
    { index: 10, title: 'Bootup Time', name: 'last_bootup_time', orderable: true, visible: true, html: (r) => bootMs(r.last_bootup_time) },
    { index: 11, title: 'Shutdown Time', name: 'last_shutdown_time', orderable: true, visible: true, html: (r) => bootMs(r.last_shutdown_time) },
    { index: 12, title: 'CPU temp', name: 'cpu_temp', orderable: true, visible: true, html: (r) => (r.cpu_temp && Number(r.cpu_temp) !== 0 ? temperature(r.cpu_temp) : dash()) },
    { index: 13, title: 'Storage temp', name: 'hdd_temp', orderable: true, visible: true, html: (r) => (r.hdd_temp && Number(r.hdd_temp) !== 0 ? temperature(r.hdd_temp) : dash()) },
    { index: 14, title: 'Antivirus status', name: 'antivirus_status', orderable: true, visible: true, html: (r) => guard(r.antivirus_status) },
    { index: 15, title: 'Firewall status', name: 'firewall_status', orderable: true, visible: true, html: (r) => guard(r.firewall_status) },
    { index: 16, title: 'Startup apps', name: 'startup_applications', orderable: true, visible: true, html: (r) => countLabel(r.startup_applications, 'label-success') },
    {
      index: 17,
      title: 'Event Viewer Errors',
      name: 'ID',
      orderable: true,
      visible: true,
      html: (r) => (r.last_critical_logs ? `<span class="label label-danger"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> ${escapeHtml(r.last_critical_logs)}</span>` : dash()),
    },
    { index: 18, title: 'Changes (24h)', name: 'changes_24h', orderable: true, visible: true, html: (r) => (Number(r.changes_24h) > 0 ? countLabel(r.changes_24h, 'label-warning') : dash()) },
    { index: 19, title: 'Last activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => fromNowOrDash(r.DataAccesareDM, now()) },
    { index: 20, title: 'External IP', name: 'external_ip', orderable: true, visible: true, html: (r) => textOrDash(r.external_ip) },
    { index: 21, title: 'Internal IP', name: 'internal_ip', orderable: true, visible: true, html: (r) => textOrDash(r.internal_ip) },
    { index: 22, title: 'Actions', orderable: false, visible: true, width: 150, html: (r) => r.actions || dash() },
    {
      index: 23,
      title: '',
      titleHtml: '<center><input type="checkbox" class="allDevices"/></center>',
      orderable: false,
      visible: true,
      html: (r) => `<center><input value="${r.deviceId}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>`,
    },
  ]
}

function bytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let i = 0
  let v = n
  while (v >= 1024 && i < units.length - 1) {
    v = v / 1024
    i++
  }
  return (i === 0 ? Math.round(v) : v.toFixed(v >= 100 ? 0 : 1)) + ' ' + units[i]
}

const STATUS_LABELS: Record<string, { cls: string; text: string }> = {
  healthy: { cls: 'label-success', text: 'Healthy' },
  degraded: { cls: 'label-warning', text: 'Degraded' },
  down: { cls: 'label-danger', text: 'Down' },
  unknown: { cls: 'label-default', text: 'Unknown' },
}

/**
 * Starea serviciului de pe server - insigna, si sub ea TOT ce a dus la ea.
 *
 * Insigna spune un cuvant; sfatul de deasupra spune de ce. Ordinea din
 * balonas nu e intamplatoare: intai ce e de facut (`next_action`), apoi
 * motivele, si abia la coada rezumatul. Cine se opreste dupa prima propozitie
 * a citit deja partea care conteaza.
 */
function serviceStatusCell(r: DeviceRow): string {
  const service = r.server_service
  if (!service?.status) return dash()

  const mark = STATUS_LABELS[service.status] ?? STATUS_LABELS.unknown
  let tip = service.health?.summary ? service.health.summary : service.status
  if (service.reasons?.length) {
    tip = service.reasons.map((x) => x.text).join('; ') + (tip ? ' — ' + tip : '')
  }
  if (service.next_action?.text) {
    tip = service.next_action.text + (tip ? ' — ' + tip : '')
  }

  return `<span class="label ${mark.cls}" data-toggle="tooltip" data-placement="top" title="${escapeHtml(tip)}">${mark.text}</span>`
}

/**
 * Sanatatea serviciului: ce e de facut, scris in clar.
 *
 * Cand exista un pas urmator, el se scrie in locul rezumatului - "Restart
 * nginx" ii spune omului mai mult decat "2 of 3 workers running". Culoarea
 * vine din gravitatea pasului, nu din starea serviciului: un serviciu
 * "degraded" care nu cere nimic acum nu are de ce sa fie rosu.
 */
function serviceHealthCell(r: DeviceRow): string {
  const service = r.server_service
  if (!service?.health) return dash()

  const text = service.next_action?.text ? service.next_action.text : (service.health.summary ?? '')
  let tip = (service.optimizations ?? []).map((o) => o.title ?? '').filter(Boolean).join('; ')
  const upgrade = service.upgrade
  if (upgrade?.importance && upgrade.importance !== 'none') {
    const up =
      (upgrade.current ?? '') +
      (upgrade.available ? ' → ' + upgrade.available : '') +
      ' · ' +
      (upgrade.label ?? upgrade.importance)
    tip = up + (tip ? ' — ' + tip : '')
  }
  if (service.reasons?.length) {
    tip = service.reasons.map((x) => x.text).join('; ') + (tip ? ' — ' + tip : '')
  }
  const severity = service.next_action?.severity ?? service.status
  const cls = severity === 'danger' ? 'color-red' : severity === 'warning' ? 'color-warning' : ''

  return `<span class="${cls}" data-toggle="tooltip" data-placement="top" title="${escapeHtml(tip || text)}">${escapeHtml(text)}</span>`
}

/** Daca UN disc se plange, masina se plange - alegerea o face deja serverul. */
function smartCell(r: DeviceRow): string {
  const smart = r.storage?.smart
  if (!smart) return dash()
  if (smart === 'ok') {
    return '<i class="fas fa-check-circle font-20 color-green2" data-toggle="tooltip" data-placement="top" title="All disks report healthy"></i>'
  }
  if (smart === 'unsupported') {
    return '<span class="color-light-grey" data-toggle="tooltip" data-placement="top" title="The disk does not report S.M.A.R.T.">n/a</span>'
  }

  return '<i class="fas fa-exclamation-circle font-20 color-red" data-toggle="tooltip" data-placement="top" title="A disk reports a fault"></i>'
}

export function serverColumns(now: () => Date = () => new Date()): Column[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${r.deviceId}" data-type="${r.Tip}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Type',
      name: 'Tip',
      orderable: true,
      visible: true,
      width: 80,
      html: (r) =>
        `<center data-toggle="tooltip" data-id="${r.deviceId}" class="quick-sidebar-device" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon} <i class="fas font-16 fa-bolt color-danger" id="sconnection_${r.deviceId}"></i></center>`,
    },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: ownerName },
    {
      index: 3,
      title: 'Role',
      orderable: false,
      visible: true,
      html: (r) => {
        const role = r.server_service?.role || r.server_role
        if (!role?.label) return dash()
        return `<span class="label label-primary" data-toggle="tooltip" data-placement="top" title="${escapeHtml('runs ' + (role.matched || ''))}">${escapeHtml(role.label)}</span>`
      },
    },
    { index: 4, title: 'Status', orderable: false, visible: true, html: serviceStatusCell },
    { index: 5, title: 'Health', orderable: false, visible: true, html: serviceHealthCell },
    {
      index: 6,
      title: 'Free space',
      orderable: false,
      visible: true,
      html: (r) => {
        const partition = r.storage?.partition
        if (!partition || partition.percent === undefined) return dash()
        const free = Number(partition.percent)
        const colour = free < 10 ? 'danger' : free < 20 ? 'warning' : 'success'
        return `<div style="min-width:100px" data-toggle="tooltip" data-placement="top" title="${escapeHtml((partition.name || 'partition') + ' - ' + bytes(partition.free || 0) + ' free of ' + bytes(partition.size || 0))}"><span class="color-${free < 20 ? 'red' : 'dark-grey'}">${free}% free</span><div class="progress progress-xs" style="margin:4px 0 0"><div class="progress-bar progress-bar-${colour}" style="width:${Math.max(0, Math.min(100, free))}%"></div></div></div>`
      },
    },
    { index: 7, title: 'S.M.A.R.T.', orderable: false, visible: true, html: smartCell },
    { index: 8, title: 'Uptime', name: 'uptime_seconds', orderable: true, visible: true, html: (r) => (Number(r.uptime_seconds) > 0 ? shortDuration(Number(r.uptime_seconds)) : dash()) },
    { index: 9, title: 'CPU temp', name: 'cpu_temp', orderable: true, visible: true, html: (r) => (r.cpu_temp && Number(r.cpu_temp) !== 0 ? temperature(r.cpu_temp) : dash()) },
    { index: 10, title: 'Storage temp', name: 'hdd_temp', orderable: true, visible: true, html: (r) => (r.hdd_temp && Number(r.hdd_temp) !== 0 ? temperature(r.hdd_temp) : dash()) },
    { index: 11, title: 'Antivirus status', name: 'antivirus_status', orderable: true, visible: true, html: (r) => guard(r.antivirus_status) },
    { index: 12, title: 'Firewall status', name: 'firewall_status', orderable: true, visible: true, html: (r) => guard(r.firewall_status) },
    { index: 13, title: 'OS Available Updates', name: 'os_available_updates', orderable: true, visible: true, html: (r) => countLabel(r.os_available_updates, 'label-warning') },
    {
      index: 14,
      title: 'Event Viewer Errors',
      orderable: true,
      visible: true,
      html: (r) => (r.last_critical_logs ? `<span class="label label-danger"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> ${escapeHtml(r.last_critical_logs)}</span>` : dash()),
    },
    { index: 15, title: 'Changes (24h)', name: 'changes_24h', orderable: true, visible: true, html: (r) => (Number(r.changes_24h) > 0 ? countLabel(r.changes_24h, 'label-warning') : dash()) },
    { index: 16, title: 'Bootup Time', name: 'last_bootup_time', orderable: true, visible: true, html: (r) => bootMs(r.last_bootup_time) },
    { index: 17, title: 'Last login', orderable: false, visible: true, html: lastLoginCell },
    { index: 18, title: 'Last activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => fromNowOrDash(r.DataAccesareDM, now()) },
    { index: 19, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 20, title: 'Actions', orderable: false, visible: true, width: 150, html: (r) => r.actions || dash() },
    {
      index: 21,
      title: '',
      titleHtml: '<center><input type="checkbox" class="allDevices"/></center>',
      orderable: false,
      visible: true,
      html: (r) => `<center><input value="${r.deviceId}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>`,
    },
  ]
}

export function unassignedColumns(now: () => Date = () => new Date()): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.ID}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Type', orderable: true, visible: true, html: (r) => `<center data-toggle="tooltip" data-placement="top" data-id="${r.ID}" class="quick-sidebar-device" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>` },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: ownerName },
    { index: 3, title: 'Last User Logged On', orderable: false, visible: true, html: (r) => escapeHtml(r.UserLogat) },
    { index: 4, title: 'Office', name: 'office', orderable: true, visible: true, html: (r) => textOrDash(r.office) },
    { index: 5, title: 'First seen', name: 'DataAdaugare', orderable: true, visible: true, html: (r) => escapeHtml(r.DataAdaugare) },
    { index: 6, title: 'Internal IP', name: 'sLastIPIntern', orderable: true, visible: true, html: (r) => textOrDash(r.sLastIPIntern) },
    { index: 7, title: 'Mac', name: 'Mac', orderable: true, visible: true, html: (r) => (r.Mac ? `<span class="color-light-grey">${escapeHtml(String(r.Mac).replace(/-/g, ':'))}</span>` : dash()) },
    { index: 8, title: 'External IP', name: 'external_ip', orderable: true, visible: true, html: (r) => textOrDash(r.external_ip) },
    { index: 9, title: 'Location', name: 'location_name', orderable: true, visible: true, html: (r) => (r.location ? `<span data-toggle="tooltip" data-placement="top" title="Seen from this office"><i class="fas fa-map-marker-alt color-light-grey"></i> ${escapeHtml(r.location)}</span>` : dash()) },
    { index: 10, title: 'Platform', orderable: true, visible: true, html: (r) => {
      const parts = [r.os_family, r.device_role].filter(Boolean)
      return parts.length ? escapeHtml(parts.join(' / ')) : dash()
    } },
    { index: 11, title: 'Agent', name: 'sAgentVersion', orderable: true, visible: true, html: (r) => textOrDash(r.sAgentVersion) },
    { index: 12, title: 'Matched devices', name: 'matched', orderable: true, visible: true, html: (r) => (Number(r.matched) > 0 ? `<span class="badge badge-success">${escapeHtml(r.matched)}</span>` : dash()) },
    { index: 13, title: 'Last communication', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => fromNowOrDash(r.DataAccesareDM, now()) },
    { index: 14, title: '', titleHtml: '<center><input type="checkbox" class="allDevices"/></center>', orderable: false, visible: true, width: 50, html: (r) => `<center><input value="${r.ID}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>` },
  ]
}

export function applicationColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => ` <center class="show_details color-dark-grey pointer font-16" data-id="${r.ID}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Application Name', name: 'short_name', orderable: true, visible: true, html: (r) => `<span class="far fa-save"></span> ${escapeHtml(r.short_name)}` },
    { index: 2, title: 'Last Version', name: 'last_version', orderable: true, visible: true, html: (r) => (r.last_version ? escapeHtml(r.last_version) : '') },
    { index: 3, title: 'Devices', name: 'devices', orderable: true, visible: true, html: (r) => (r.devices ? `<span class="badge bg-blue">${escapeHtml(r.devices)} devices</span>` : '') },
    { index: 4, title: 'Category', name: 'category_name', orderable: true, visible: true, html: (r) => (r.category_name ? escapeHtml(r.category_name) : '<em class="color-light-grey">no category</em>') },
  ]
}

export function scheduleColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.schedule_id}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Name', name: 'schedule_name', orderable: true, visible: true, html: (r) => escapeHtml(r.schedule_name) },
    { index: 2, title: 'Commands', name: 'commands', orderable: true, visible: true, width: 100, html: (r) => `<center><span class="label label-primary">${escapeHtml(r.commands)}</span></center>` },
    { index: 3, title: 'Commands Groups', name: 'groups', orderable: true, visible: true, html: (r) => (r.groups != null ? escapeHtml(r.groups) : '-') },
    { index: 4, title: 'Devices', name: 'devices', orderable: true, visible: true, width: 100, html: (r) => `<center><span class="label label-primary">${escapeHtml(r.devices)}</span></center>` },
    { index: 5, title: '', titleHtml: '<center><input type="checkbox" class="allInputs"/></center>', orderable: false, visible: true, width: 100, html: (r) => `<center><input value="${r.schedule_id}" type="checkbox" class="oneInput"${r.__checked ? ' checked' : ''}/></center>` },
  ]
}

/**
 * #opteemumDevice - fisa unei singure masini, /opteemum/device/{id}.
 *
 * COLOANELE SUNT CELE PE CARE LE SCRIA RANDUL, NU CELE DIN CAPUL TABELULUI.
 *
 * In `device/details.php` capul avea 18 coloane, iar `DeviceTable.buildTable`
 * scria 21 de celule sub ele: de la a douasprezecea incolo, fiecare valoare
 * statea sub eticheta alteia, si ultimele trei nu aveau eticheta deloc.
 * Temperaturile si pazele - CPU temp, Storage temp, Antivirus, Firewall - erau
 * printre ele: se vedeau, dar scria altceva deasupra lor.
 *
 * Aici se tin celulele si li se dau etichetele lor. "Running applications" din
 * capul vechi nu se intoarce: era eticheta pentru care nu s-a scris niciodata o
 * valoare, si tocmai de la ea incepea decalajul.
 */
export function pageDeviceColumns(now: () => Date = () => new Date()): Column[] {
  return [
    { index: 0, title: 'Type', name: 'Tip', orderable: true, visible: true, html: (r) => `<center data-toggle="tooltip" data-id="${r.deviceId}" class="quick-sidebar-device" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon} <i class="fas font-16 fa-bolt color-danger" id="sconnection_${r.deviceId}"></i></center>` },
    { index: 1, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: ownerName },
    { index: 2, title: 'Last User Logged On', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash(r.UserLogat) },
    { index: 3, title: 'Office', name: 'office', orderable: true, visible: true, html: (r) => textOrDash(r.office) },
    { index: 4, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 5, title: 'Operating system', name: 'operating_system', orderable: true, visible: true, html: (r) => textOrDash(r.operating_system) },
    { index: 6, title: 'OS Available Updates', name: 'os_available_updates', orderable: true, visible: true, html: (r) => countLabel(r.os_available_updates, 'label-warning') },
    { index: 7, title: 'Installed Applications', name: 'installed_applications', orderable: true, visible: true, html: (r) => countLabel(r.installed_applications, 'label-success') },
    { index: 8, title: 'WEI', name: 'wei', orderable: true, visible: true, html: (r) => textOrDash(r.wei) },
    { index: 9, title: 'Bootup Time', name: 'last_bootup_time', orderable: true, visible: true, html: (r) => bootMs(r.last_bootup_time) },
    { index: 10, title: 'Shutdown Time', name: 'last_shutdown_time', orderable: true, visible: true, html: (r) => bootMs(r.last_shutdown_time) },
    { index: 11, title: 'CPU temp', name: 'cpu_temp', orderable: true, visible: true, html: (r) => (r.cpu_temp && Number(r.cpu_temp) !== 0 ? temperature(r.cpu_temp) : dash()) },
    { index: 12, title: 'Storage temp', name: 'hdd_temp', orderable: true, visible: true, html: (r) => (r.hdd_temp && Number(r.hdd_temp) !== 0 ? temperature(r.hdd_temp) : dash()) },
    { index: 13, title: 'Antivirus status', name: 'antivirus_status', orderable: true, visible: true, html: (r) => guard(r.antivirus_status) },
    { index: 14, title: 'Firewall status', name: 'firewall_status', orderable: true, visible: true, html: (r) => guard(r.firewall_status) },
    { index: 15, title: 'Startup apps', name: 'startup_applications', orderable: true, visible: true, html: (r) => countLabel(r.startup_applications, 'label-success') },
    { index: 16, title: 'Event Viewer Errors', name: 'ID', orderable: true, visible: true, html: (r) => (r.last_critical_logs ? `<span class="label label-danger"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> ${escapeHtml(r.last_critical_logs)}</span>` : dash()) },
    { index: 17, title: 'Last activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => fromNowOrDash(r.DataAccesareDM, now()) },
    { index: 18, title: 'External IP', name: 'external_ip', orderable: true, visible: true, html: (r) => textOrDash(r.external_ip) },
    { index: 19, title: 'Actions', orderable: false, visible: true, width: 150, html: (r) => r.actions || dash() },
    { index: 20, title: '', titleHtml: '<center><input type="checkbox" class="allDevices"/></center>', orderable: false, visible: true, html: (r) => `<center><input value="${r.deviceId}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>` },
  ]
}

export function remoteColumns(now: () => Date = () => new Date()): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.ID}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Type', name: 'Tip', orderable: true, visible: true, html: (r) => `<center data-toggle="tooltip" data-placement="top" data-id="${r.ID}" class="quick-sidebar-device" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>` },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: ownerName },
    { index: 3, title: 'Last User Logged On', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash(r.UserLogat) },
    { index: 4, title: 'Office', name: 'office', orderable: true, visible: true, html: (r) => textOrDash(r.office) },
    { index: 5, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 6, title: 'Last remote connection', name: 'last_remote', orderable: true, visible: true, html: (r) => fromNowOrDash(r.last_remote, now()) },
    { index: 7, title: 'Last activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => fromNowOrDash(r.DataAccesareDM, now()) },
    { index: 8, title: 'Connections', name: 'connections', orderable: true, visible: true, html: (r) => escapeHtml(r.connections) },
    { index: 9, title: 'Remote connect', orderable: false, visible: true, width: 100, html: (r) => String(r.remote_icon || dash()) },
  ]
}
